<template>
  <ContentWrap title="日历量价管理">
    <el-form :model="query" inline class="-mb-15px">
      <el-form-item label="店铺" required>
        <el-select v-model="query.shopId" filterable class="!w-220px" @change="loadCalendar">
          <el-option v-for="item in shops" :key="item.id" :label="item.name" :value="item.id" />
        </el-select>
      </el-form-item>
      <el-form-item label="房型" required>
        <el-select v-model="query.spuId" filterable class="!w-220px" @change="loadCalendar">
          <el-option v-for="item in spus" :key="item.id" :label="item.name" :value="item.id" />
        </el-select>
      </el-form-item>
      <el-form-item label="月份">
        <el-date-picker v-model="query.month" type="month" value-format="YYYY-MM" @change="loadCalendar" />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="loadCalendar"><Icon icon="ep:search" />查询</el-button>
        <el-button v-hasPermi="['hotel:price-inventory:update']" @click="openBatch">
          <Icon icon="ep:setting" />批量设置
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <div v-loading="loading" class="calendar">
      <div v-for="week in weekLabels" :key="week" class="calendar__week">{{ week }}</div>
      <button
        v-for="cell in calendarCells"
        :key="cell.date"
        type="button"
        class="calendar__cell"
        :class="{ 'is-outside': !cell.currentMonth, 'is-today': cell.today, 'is-selected': isSelected(cell.date) }"
        @pointerdown.prevent="startSelection(cell)"
        @pointerenter="extendSelection(cell)"
        @pointerup.prevent="finishSelection"
        @click="openDay(cell)"
      >
        <span class="calendar__day">{{ cell.day }}</span>
        <div class="calendar__columns">
          <div class="calendar__price">
            <small>房价</small><strong>{{ money(cell.data?.price) }}</strong>
            <small>加床 {{ money(cell.data?.extraBedPrice) }}</small>
          </div>
          <div class="calendar__stock">
            <small>库存</small><strong>{{ cell.data?.availableCount ?? 0 }}/{{ cell.data?.totalCount ?? 0 }}</strong>
            <small>锁 {{ cell.data?.lockedCount ?? 0 }} / 售 {{ cell.data?.soldCount ?? 0 }}</small>
          </div>
        </div>
      </button>
    </div>
  </ContentWrap>

  <Dialog v-model="dayVisible" title="编辑当日量价" width="480px">
    <el-form :model="dayForm" label-width="100px">
      <el-form-item label="日期"><el-input :model-value="dayForm.date" disabled /></el-form-item>
      <el-form-item label="房价"><el-input-number v-model="dayForm.price" :min="0" :precision="2" /></el-form-item>
      <el-form-item label="加床价格"><el-input-number v-model="dayForm.extraBedPrice" :min="0" :precision="2" /></el-form-item>
      <el-form-item label="总库存"><el-input-number v-model="dayForm.totalCount" :min="dayMinStock" /></el-form-item>
      <el-alert v-if="dayMinStock > 0" :closable="false" type="info" :title="`该日已有锁定/已售 ${dayMinStock} 间，总库存不能低于该数值`" />
    </el-form>
    <template #footer>
      <el-button @click="dayVisible = false">取消</el-button>
      <el-button type="primary" :loading="saving" @click="saveDay">保存</el-button>
    </template>
  </Dialog>

  <Dialog v-model="batchVisible" title="批量设置量价" width="560px">
    <el-form ref="batchRef" :model="batchForm" :rules="batchRules" label-width="100px">
      <el-form-item label="日期范围" prop="dateRange">
        <el-date-picker v-model="batchForm.dateRange" type="daterange" value-format="YYYY-MM-DD" />
      </el-form-item>
      <el-form-item label="适用星期" prop="weekdays">
        <el-checkbox-group v-model="batchForm.weekdays">
          <el-checkbox-button v-for="item in weekdayOptions" :key="item.value" :value="item.value">{{ item.label }}</el-checkbox-button>
        </el-checkbox-group>
      </el-form-item>
      <el-form-item label="房价" prop="price"><el-input-number v-model="batchForm.price" :min="0" :precision="2" /></el-form-item>
      <el-form-item label="加床价格" prop="extraBedPrice"><el-input-number v-model="batchForm.extraBedPrice" :min="0" :precision="2" /></el-form-item>
      <el-form-item label="总库存" prop="totalCount"><el-input-number v-model="batchForm.totalCount" :min="0" /></el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="batchVisible = false">取消</el-button>
      <el-button type="primary" :loading="saving" @click="saveBatch">保存</el-button>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import * as PriceInventoryApi from '@/api/hotel/priceInventory'
import { getShopSimpleList } from '@/api/hotel/platformShop'
import { getSpuSimpleList } from '@/api/mall/product/spu'

defineOptions({ name: 'HotelPriceInventory' })

const message = useMessage()
const loading = ref(false)
const saving = ref(false)
const shops = ref<any[]>([])
const spus = ref<any[]>([])
const records = ref<PriceInventoryApi.PriceInventoryCalendarVO[]>([])
const query = reactive<any>({ shopId: undefined, spuId: undefined, month: dayjs().format('YYYY-MM') })
const weekLabels = ['周一', '周二', '周三', '周四', '周五', '周六', '周日']
const weekdayOptions = weekLabels.map((label, index) => ({ label, value: index + 1 }))
const selecting = ref(false)
const selectionStart = ref('')
const selectionEnd = ref('')
const suppressClick = ref(false)

const period = computed(() => {
  const start = dayjs(`${query.month}-01`)
  return { start, end: start.endOf('month') }
})
const recordMap = computed(() => new Map(records.value.map((item) => [item.date, item])))
const calendarCells = computed(() => {
  const start = period.value.start.startOf('week').add(1, 'day')
  const normalized = start.isAfter(period.value.start) ? start.subtract(7, 'day') : start
  return Array.from({ length: 42 }, (_, index) => {
    const date = normalized.add(index, 'day')
    const dateText = date.format('YYYY-MM-DD')
    return { date: dateText, day: date.date(), currentMonth: date.month() === period.value.start.month(), today: dateText === dayjs().format('YYYY-MM-DD'), data: recordMap.value.get(dateText) }
  })
})

const money = (value?: number | null) => value == null ? '-' : `¥${Number(value).toFixed(2)}`
const loadCalendar = async () => {
  if (!query.shopId || !query.spuId) return
  loading.value = true
  try {
    records.value = await PriceInventoryApi.queryCalendar({ shopId: query.shopId, spuId: query.spuId, startDate: period.value.start.format('YYYY-MM-DD'), endDate: period.value.end.format('YYYY-MM-DD') })
  } finally { loading.value = false }
}

const dayVisible = ref(false)
const dayMinStock = ref(0)
const dayForm = reactive({ date: '', price: 0, extraBedPrice: 0, totalCount: 0 })
const openDay = (cell: any) => {
  if (suppressClick.value) {
    suppressClick.value = false
    return
  }
  if (!cell.currentMonth || !query.shopId || !query.spuId) return
  dayForm.date = cell.date
  dayForm.price = Number(cell.data?.price || 0)
  dayForm.extraBedPrice = Number(cell.data?.extraBedPrice || 0)
  dayForm.totalCount = Number(cell.data?.totalCount || 0)
  dayMinStock.value = Number(cell.data?.lockedCount || 0) + Number(cell.data?.soldCount || 0)
  dayVisible.value = true
}
const saveRecords = async (data: Omit<PriceInventoryApi.PriceInventorySaveVO, 'shopId' | 'spuId'>[]) => {
  saving.value = true
  try {
    await PriceInventoryApi.batchSet(data.map((item) => ({ ...item, shopId: query.shopId, spuId: query.spuId })))
    message.success('量价保存成功')
    dayVisible.value = false
    batchVisible.value = false
    await loadCalendar()
  } finally { saving.value = false }
}
const saveDay = () => saveRecords([{ ...dayForm }])

const batchVisible = ref(false)
const batchRef = ref()
const batchForm = reactive({ dateRange: [] as string[], weekdays: [1, 2, 3, 4, 5, 6, 7], price: 0, extraBedPrice: 0, totalCount: 0 })
const batchRules = { dateRange: [{ required: true, message: '请选择日期范围' }], weekdays: [{ required: true, message: '请选择适用星期' }] }
const openBatch = () => {
  if (!query.shopId || !query.spuId) return message.warning('请先选择店铺和房型')
  batchVisible.value = true
}
const selectedRange = computed(() => {
  if (!selectionStart.value || !selectionEnd.value) return []
  return [selectionStart.value, selectionEnd.value].sort()
})
const isSelected = (date: string) => selectedRange.value.length === 2
  && date >= selectedRange.value[0] && date <= selectedRange.value[1]
const startSelection = (cell: any) => {
  if (!cell.currentMonth || !query.shopId || !query.spuId) return
  selecting.value = true
  suppressClick.value = false
  selectionStart.value = cell.date
  selectionEnd.value = cell.date
}
const extendSelection = (cell: any) => {
  if (!selecting.value || !cell.currentMonth) return
  if (selectionEnd.value !== cell.date) suppressClick.value = true
  selectionEnd.value = cell.date
}
const finishSelection = () => {
  if (!selecting.value) return
  selecting.value = false
  if (!suppressClick.value) return
  batchForm.dateRange = [...selectedRange.value]
  batchVisible.value = true
}
const stopSelection = () => { selecting.value = false }
const saveBatch = async () => {
  await batchRef.value?.validate()
  const rows: Omit<PriceInventoryApi.PriceInventorySaveVO, 'shopId' | 'spuId'>[] = []
  for (let date = dayjs(batchForm.dateRange[0]); !date.isAfter(dayjs(batchForm.dateRange[1])); date = date.add(1, 'day')) {
    const weekday = date.day() === 0 ? 7 : date.day()
    if (batchForm.weekdays.includes(weekday)) rows.push({ date: date.format('YYYY-MM-DD'), price: batchForm.price, extraBedPrice: batchForm.extraBedPrice, totalCount: batchForm.totalCount })
  }
  if (!rows.length) return message.warning('日期范围内没有符合星期条件的日期')
  await saveRecords(rows)
}

onMounted(async () => {
  window.addEventListener('pointerup', stopSelection)
  const [shopList, spuList] = await Promise.all([getShopSimpleList(), getSpuSimpleList()])
  shops.value = shopList || []
  spus.value = spuList || []
  query.shopId = shops.value[0]?.id
  query.spuId = spus.value[0]?.id
  await loadCalendar()
})
onBeforeUnmount(() => window.removeEventListener('pointerup', stopSelection))
</script>

<style scoped lang="scss">
.calendar { display: grid; grid-template-columns: repeat(7, minmax(120px, 1fr)); border: 1px solid var(--el-border-color); }
.calendar__week { padding: 10px; text-align: center; font-weight: 600; background: var(--el-fill-color-light); border-right: 1px solid var(--el-border-color); }
.calendar__cell { min-height: 112px; padding: 8px; text-align: left; background: #fff; border: 0; border-top: 1px solid var(--el-border-color); border-right: 1px solid var(--el-border-color); cursor: pointer; }
.calendar__cell:hover { background: var(--el-color-primary-light-9); }
.calendar__cell.is-selected { background: var(--el-color-primary-light-8); box-shadow: inset 0 0 0 2px var(--el-color-primary); }
.calendar__cell.is-outside { opacity: .35; cursor: default; }
.calendar__cell.is-today .calendar__day { color: var(--el-color-primary); font-weight: 700; }
.calendar__day { display: block; margin-bottom: 8px; }
.calendar__columns { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; }
.calendar__price, .calendar__stock { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.calendar__price { padding-right: 6px; border-right: 1px solid var(--el-border-color-lighter); }
.calendar small { color: var(--el-text-color-secondary); }
.calendar strong { font-size: 13px; }
@media (max-width: 1100px) { .calendar { overflow-x: auto; grid-template-columns: repeat(7, 130px); } }
</style>
